const DISCORD_AVATAR_HOSTS = new Set(['cdn.discordapp.com', 'media.discordapp.net']);
const REFRESH_INTERVAL = 120000;
const SCORE_FORMATTER = new Intl.NumberFormat('es-MX');

function cleanText(value, maxLength) {
  return Array.from(String(value || ''))
    .filter((character) => {
      const codePoint = character.codePointAt(0);
      return codePoint > 31 && codePoint !== 127;
    })
    .join('')
    .trim()
    .slice(0, maxLength);
}

function getAvatarUrl(value) {
  try {
    const url = new URL(String(value || ''));

    if (url.protocol === 'https:' && DISCORD_AVATAR_HOSTS.has(url.hostname)) {
      return url.href;
    }
  } catch (_error) {
    return '';
  }

  return '';
}

function formatPeriod(period) {
  if (!/^\d{4}-(0[1-9]|1[0-2])$/.test(period)) return '';

  const date = new Date(period + '-15T12:00:00.000Z');

  return new Intl.DateTimeFormat('es-MX', {
    year: 'numeric',
    month: 'long',
    timeZone: 'America/Mexico_City',
  }).format(date);
}

function normalizePlayers(payload, metric) {
  if (!payload || !Array.isArray(payload.players)) {
    return [];
  }

  const usedPositions = new Set();
  const usedPlayers = new Set();

  return payload.players
    .map((player) => {
      const position = Number(player?.position);
      const discordId = String(player?.discordId || '').trim();
      const name = cleanText(player?.displayName || player?.username, 80);
      const scoreValue = Number(player?.[metric]);
      const score = Number.isInteger(scoreValue)
        && scoreValue >= 0
        && scoreValue <= 10000000
        ? scoreValue
        : null;

      if (
        !Number.isInteger(position)
        || position < 1
        || position > 3
        || !/^\d{17,20}$/.test(discordId)
        || !name
        || usedPositions.has(position)
        || usedPlayers.has(discordId)
      ) {
        return null;
      }

      usedPositions.add(position);
      usedPlayers.add(discordId);

      return {
        position,
        discordId,
        name,
        username: cleanText(player?.username, 32),
        avatarUrl: getAvatarUrl(player?.avatarUrl),
        profileUrl: 'https://discord.com/users/' + discordId,
        [metric]: score,
      };
    })
    .filter(Boolean)
    .sort((first, second) => first.position - second.position);
}

export function normalizeTopPlayers(payload) {
  return normalizePlayers(payload, 'murals');
}

export function normalizeWinsPlayers(payload) {
  return normalizePlayers(payload, 'wins');
}

export function normalizeRankings(payload) {
  const monthly = payload?.monthly || {};
  const historical = payload?.historical || { players: payload?.players };
  const period = /^\d{4}-(0[1-9]|1[0-2])$/.test(String(monthly.period || ''))
    ? String(monthly.period)
    : '';

  return {
    monthly: {
      period,
      label: cleanText(monthly.label, 80) || formatPeriod(period),
      players: normalizeTopPlayers(monthly),
    },
    historical: {
      players: normalizeTopPlayers(historical),
    },
  };
}

function createPlayerCard(position, metric) {
  const card = document.createElement('article');
  card.className = 'player-card player-card--' + position;
  card.dataset.playerCard = String(position);
  card.setAttribute('aria-label', ['Primer', 'Segundo', 'Tercer'][position - 1] + ' lugar');
  card.setAttribute('aria-busy', 'true');

  const rank = document.createElement('span');
  rank.className = 'player-card__rank';
  rank.setAttribute('aria-hidden', 'true');
  rank.textContent = 'TOP ' + position;

  const avatar = document.createElement('div');
  avatar.className = 'player-card__avatar';

  const image = document.createElement('img');
  image.src = 'images/logo-320.webp';
  image.width = position === 1 ? 162 : 144;
  image.height = position === 1 ? 162 : 144;
  image.alt = '';
  image.dataset.playerAvatar = '';
  avatar.append(image);

  const name = document.createElement('h4');
  name.dataset.playerName = '';
  name.textContent = 'Jugador';

  const username = document.createElement('p');
  username.className = 'player-card__username';
  username.dataset.playerUsername = '';
  username.textContent = 'Perfil de Discord';

  const score = document.createElement('p');
  score.className = 'player-card__murals player-card__score';
  score.dataset.playerScore = '';
  score.textContent = metric === 'wins' ? 'Calculando victorias' : 'Calculando murales';

  const profile = document.createElement('button');
  profile.className = 'button ' + (position === 1 ? 'button--primary' : 'button--secondary');
  profile.type = 'button';
  profile.dataset.actionUrl = 'https://discord.com';
  profile.dataset.actionMode = 'new-tab';
  profile.dataset.playerProfile = '';
  profile.textContent = 'Ver perfil';

  card.append(rank, avatar, name, username, score, profile);
  return card;
}

function ensureCards(board) {
  const podium = board.querySelector('[data-top-players-podium]');

  if (!podium || podium.children.length > 0) return;

  const metric = board.dataset.rankingMetric === 'wins' ? 'wins' : 'murals';

  for (let position = 1; position <= 3; position += 1) {
    podium.append(createPlayerCard(position, metric));
  }
}

function setBoardStatus(board, message) {
  const podium = board.querySelector('[data-top-players-podium]');
  const status = board.querySelector('[data-top-players-status]');

  if (podium) podium.hidden = true;

  if (status) {
    status.hidden = false;
    status.textContent = message;
  }
}

function formatScore(value, metric) {
  if (value === null) return 'Conteo en actualización';

  if (metric === 'wins') {
    return SCORE_FORMATTER.format(value) + (value === 1 ? ' victoria' : ' victorias');
  }

  return SCORE_FORMATTER.format(value) + (value === 1 ? ' mural' : ' murales');
}

function renderBoard(board, ranking) {
  ensureCards(board);

  const metric = board.dataset.rankingMetric === 'wins' ? 'wins' : 'murals';
  const cards = board.querySelectorAll('[data-player-card]');
  const podium = board.querySelector('[data-top-players-podium]');
  const status = board.querySelector('[data-top-players-status]');

  for (const card of cards) {
    card.hidden = true;
    card.setAttribute('aria-busy', 'true');
  }

  if (ranking.players.length === 0) {
    setBoardStatus(board, board.dataset.emptyMessage || 'Todavía no hay jugadores clasificados.');
    return;
  }

  for (const player of ranking.players) {
    const card = board.querySelector('[data-player-card="' + player.position + '"]');
    if (!card) continue;

    const image = card.querySelector('[data-player-avatar]');
    const name = card.querySelector('[data-player-name]');
    const username = card.querySelector('[data-player-username]');
    const score = card.querySelector('[data-player-score]');
    const profile = card.querySelector('[data-player-profile]');

    if (image) {
      image.src = player.avatarUrl || 'images/logo-320.webp';
      image.alt = 'Avatar de ' + player.name;
      image.dataset.fallback = 'images/logo-320.webp';
    }

    if (name) name.textContent = player.name;
    if (username) {
      username.textContent = player.username ? '@' + player.username : 'Perfil de Discord';
    }
    if (score) score.textContent = formatScore(player[metric], metric);
    if (profile) {
      profile.dataset.actionUrl = player.profileUrl;
      profile.setAttribute('aria-label', 'Ver el perfil de ' + player.name + ' en Discord');
    }

    card.hidden = false;
    card.removeAttribute('aria-busy');
  }

  if (status) status.hidden = true;
  if (podium) podium.hidden = false;
}

function renderUpdatedAt(root, selector, updatedAt) {
  const updated = root.querySelector(selector);
  if (!updated) return;

  if (typeof updatedAt !== 'string' || updatedAt.trim() === '') {
    updated.hidden = true;
    updated.textContent = '';
    updated.removeAttribute('datetime');
    return;
  }

  const date = new Date(updatedAt);

  if (!Number.isFinite(date.getTime())) {
    updated.hidden = true;
    updated.textContent = '';
    updated.removeAttribute('datetime');
    return;
  }

  const formattedDate = new Intl.DateTimeFormat('es-MX', {
    dateStyle: 'long',
    timeStyle: 'short',
  }).format(date);

  updated.textContent = (updated.dataset.updatedLabel || 'Clasificación actualizada el ')
    + formattedDate;
  updated.dateTime = date.toISOString();
  updated.hidden = false;
}

function renderMuralRankings(root, payload) {
  const rankings = normalizeRankings(payload);
  const monthlyBoard = root.querySelector('[data-ranking-board="monthly"]');
  const historicalBoard = root.querySelector('[data-ranking-board="historical"]');
  const period = root.querySelector('[data-ranking-period]');

  if (monthlyBoard) renderBoard(monthlyBoard, rankings.monthly);
  if (historicalBoard) renderBoard(historicalBoard, rankings.historical);

  if (period) {
    period.textContent = rankings.monthly.label || 'Mes actual';
    if (rankings.monthly.period) period.dateTime = rankings.monthly.period;
  }

  renderUpdatedAt(root, '[data-top-players-updated]', payload?.updatedAt);
}

function renderWinsRanking(root, payload) {
  const winsBoard = root.querySelector('[data-ranking-board="wins"]');
  if (winsBoard) renderBoard(winsBoard, { players: normalizeWinsPlayers(payload) });
  renderUpdatedAt(root, '[data-top-wins-updated]', payload?.updatedAt);
}

async function requestRanking(endpoint) {
  const response = await fetch(endpoint, {
    cache: 'no-store',
    headers: { Accept: 'application/json' },
  });

  if (!response.ok) {
    throw new Error('Ranking request failed with ' + response.status);
  }

  return await response.json();
}

async function loadMuralRanking(root) {
  try {
    const endpoint = root.dataset.rankingEndpoint || 'api/ranking.php';
    renderMuralRankings(root, await requestRanking(endpoint));
  } catch (_error) {
    for (const board of root.querySelectorAll('[data-ranking-panel="mural"] [data-ranking-board]')) {
      setBoardStatus(board, 'Top Mural estará disponible cuando el bot quede conectado.');
    }
  }
}

async function loadWinsRanking(root) {
  try {
    const endpoint = root.dataset.winsEndpoint || 'api/wins.php';
    renderWinsRanking(root, await requestRanking(endpoint));
  } catch (_error) {
    const board = root.querySelector('[data-ranking-board="wins"]');
    if (board) {
      setBoardStatus(board, 'Top Wins estará disponible después de la primera sincronización.');
    }
  }
}

export function initTopPlayers() {
  const root = document.querySelector('[data-top-players]');
  if (!root) return;

  loadMuralRanking(root);
  loadWinsRanking(root);

  window.setInterval(() => {
    if (!document.hidden) {
      loadMuralRanking(root);
      loadWinsRanking(root);
    }
  }, REFRESH_INTERVAL);
}
